import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { describeHttpError, isPendingFeature } from '../api/http';
import {
  EMPTY_BRANDING,
  fetchBranding,
  parseBranding,
  saveBranding,
  type Branding,
} from '../data/admin';

/**
 * Branding del tenant en tiempo de ejecución (RF-21, PRD §12 white-label).
 *
 * `src/index.css` declara el tema como variables CSS justo para esto: el panel
 * las sobreescribe en `:root` y no recompila nada. Una variable en línea gana
 * siempre a la regla de la hoja de estilos —incluida la de
 * `prefers-color-scheme`— así que el modo oscuro hay que resolverlo aquí: se
 * observa el esquema del sistema y se aplica la variante que toca. Sin eso, poner
 * un color de fondo claro dejaría el panel ilegible de noche.
 *
 * El almacén local es un **respaldo**, no la fuente: mientras el backend no
 * exponga `/tenants/current/branding`, un administrador puede dejar el branding
 * aplicado en su navegador y verlo funcionar de punta a punta. Cuando la ruta
 * exista, lo que diga el backend manda y lo local se descarta.
 */

const STORAGE_KEY = 'occ.branding';

/** De dónde salió lo que se está viendo. Se enseña, para no confundir uno con otro. */
export type BrandingSource = 'backend' | 'local' | 'default';

type BrandingContextValue = {
  branding: Branding;
  source: BrandingSource;
  /** Motivo por el que el backend no sirvió el branding, si es que no lo sirvió. */
  backendNotice: string | null;
  /** Aplica sin guardar: la vista previa de la pantalla de branding. */
  preview: (branding: Branding | null) => void;
  save: (branding: Branding) => Promise<void>;
  reset: () => Promise<void>;
};

const BrandingContext = createContext<BrandingContextValue | null>(null);

const loadLocal = (): Branding | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? parseBranding(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

const storeLocal = (branding: Branding | null): void => {
  try {
    if (branding) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(branding));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Almacenamiento bloqueado: el branding dura lo que la pestaña.
  }
};

/** Sin un solo campo con contenido no hay marca: es el «tema por omisión». */
const esVacio = (branding: Branding): boolean =>
  Object.values(branding).every((valor) => !valor.trim());

const prefersDark = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

/**
 * Escribe las variables en `:root`.
 *
 * Los valores vacíos se **quitan** en vez de escribirse como cadena vacía: una
 * variable declarada vacía anula la de la hoja de estilos y dejaría el color sin
 * definir, que en `background` se pinta transparente.
 */
const applyBranding = (branding: Branding, dark: boolean): void => {
  const root = document.documentElement;
  const surface = dark ? branding.surfaceDark : branding.surfaceLight;
  const text = dark ? branding.textDark : branding.textLight;

  const set = (name: string, value: string) => {
    if (value.trim()) root.style.setProperty(name, value.trim());
    else root.style.removeProperty(name);
  };

  set('--occ-brand', branding.brandColor);
  set('--occ-surface', surface);
  set('--occ-text', text);
};

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<Branding>(() => loadLocal() ?? EMPTY_BRANDING);
  const [source, setSource] = useState<BrandingSource>(() => (loadLocal() ? 'local' : 'default'));
  const [backendNotice, setBackendNotice] = useState<string | null>(null);
  const [previewed, setPreviewed] = useState<Branding | null>(null);
  const [dark, setDark] = useState(prefersDark);

  // El esquema del sistema puede cambiar con la pantalla ya abierta (modo
  // automático del sistema operativo al atardecer, por ejemplo).
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchBranding()
      .then((branding) => {
        if (cancelled) return;
        setStored(branding);
        setSource('backend');
        setBackendNotice(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setBackendNotice(
          isPendingFeature(error)
            ? 'El backend todavía no expone el branding del tenant; se aplica lo guardado en este navegador.'
            : describeHttpError(error),
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const effective = previewed ?? stored;

  useEffect(() => {
    applyBranding(effective, dark);
  }, [effective, dark]);

  const save = useCallback(async (branding: Branding) => {
    try {
      const saved = await saveBranding(branding);
      setStored(saved);
      setSource('backend');
      setBackendNotice(null);
      storeLocal(null);
    } catch (error) {
      if (!isPendingFeature(error)) throw error;
      // La ruta no existe: se guarda en local y se dice claramente que eso no
      // llega a los demás operadores. Fallar en seco escondería una función que
      // ya está terminada salvo por el endpoint.
      setStored(branding);
      setSource('local');
      storeLocal(branding);
      setBackendNotice(
        'Guardado solo en este navegador: el backend todavía no expone /tenants/current/branding, así que el resto del equipo no lo verá.',
      );
    } finally {
      setPreviewed(null);
    }
  }, []);

  /**
   * Quitar el branding **es un guardado**, no solo un borrado local.
   *
   * Antes esto se limitaba a vaciar el estado y el respaldo, así que el botón dejaba
   * la pantalla como si la marca se hubiera quitado mientras la fila del inquilino
   * seguía intacta: bastaba recargar para que volviera, y el resto del equipo nunca
   * dejó de verla. Peor que no tener el botón, porque decía que había funcionado.
   *
   * Se manda un `PUT` con todos los campos vacíos porque es lo que el backend
   * entiende por «sin branding» — no hay `DELETE` en esa ruta.
   */
  const reset = useCallback(async () => {
    try {
      const saved = await saveBranding(EMPTY_BRANDING);
      setStored(saved);
      // El distintivo se decide por lo que **volvió**, no por lo que se mandó: si el
      // backend devolviera algo con contenido, decir «tema por omisión» sería mentir
      // sobre lo que se está viendo.
      setSource(esVacio(saved) ? 'default' : 'backend');
      setBackendNotice(null);
      storeLocal(null);
    } catch (error) {
      if (!isPendingFeature(error)) throw error;
      setStored(EMPTY_BRANDING);
      setSource('default');
      storeLocal(null);
      setBackendNotice(
        'Quitado solo en este navegador: el backend todavía no expone /tenants/current/branding, así que el resto del equipo seguirá viendo la marca anterior.',
      );
    } finally {
      setPreviewed(null);
    }
  }, []);

  const value = useMemo<BrandingContextValue>(
    () => ({
      branding: effective,
      source,
      backendNotice,
      preview: setPreviewed,
      save,
      reset,
    }),
    [effective, source, backendNotice, save, reset],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  const context = useContext(BrandingContext);
  if (!context) throw new Error('useBranding fuera de BrandingProvider');
  return context;
}
